// src/pages/Overview.js - Complete Enhanced Dashboard
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, BarChart, Bar, Cell, PieChart, Pie,
  Legend
} from 'recharts';

function Overview() {
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalDrivers: 0,
    totalRides: 0,
    todayRides: 0,
    totalRevenue: 0,
    todayRevenue: 0,
    pendingDrivers: 0,
    activeRides: 0,
    completionRate: 0,
  });
  const [recentRides, setRecentRides] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [rideStatusData, setRideStatusData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      if (autoRefresh) fetchData();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch stats
      const statsRes = await axios.get(`${API_URL}/stats`, { headers });
      const statsData = statsRes.data || {};
      
      // Fetch recent rides
      const ridesRes = await axios.get(`${API_URL}/recent-rides`, { headers });
      const rides = ridesRes.data.rides || [];
      
      // Calculate ride status distribution
      const statusCount = {};
      rides.forEach(r => {
        const s = r.status || 'pending';
        statusCount[s] = (statusCount[s] || 0) + 1;
      });
      const statusData = Object.entries(statusCount).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value
      }));

      // Generate revenue trend (last 7 days)
      const trend = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayRides = rides.filter(r => r.created_at?.startsWith(dateStr));
        const revenue = dayRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);
        trend.push({ date: dateStr, revenue, rides: dayRides.length });
      }

      // Generate activity feed from recent rides
      const activities = rides.slice(0, 10).map(r => ({
        id: r.id,
        type: r.status === 'completed' ? 'ride_completed' : 
              r.status === 'cancelled' ? 'ride_cancelled' : 'ride_created',
        title: r.status === 'completed' ? 'Ride Completed' :
               r.status === 'cancelled' ? 'Ride Cancelled' : 'Ride Created',
        description: `Ride #${r.id} - ${r.customer_name || 'Customer'} ` +
          (r.status === 'completed' ? `UGX ${(r.total_fare || 0).toLocaleString()}` : ''),
        time: r.created_at,
        icon: r.status === 'completed' ? '✅' :
              r.status === 'cancelled' ? '❌' : '🚗',
        color: r.status === 'completed' ? '#10b981' :
               r.status === 'cancelled' ? '#ef4444' : '#3b82f6',
      }));

      const completedRides = rides.filter(r => r.status === 'completed');
      const completionRate = rides.length > 0 ? (completedRides.length / rides.length * 100) : 0;

      setStats({
        totalCustomers: statsData.total_customers || 0,
        totalDrivers: statsData.total_drivers || 0,
        totalRides: statsData.total_rides || 0,
        todayRides: statsData.today_rides || 0,
        totalRevenue: statsData.total_revenue || 0,
        todayRevenue: statsData.today_revenue || 0,
        pendingDrivers: statsData.pending_drivers || 0,
        activeRides: rides.filter(r => r.status === 'in_progress' || r.status === 'accepted').length,
        completionRate: completionRate,
      });
      
      setRecentRides(rides.slice(0, 10));
      setRevenueTrend(trend);
      setRideStatusData(statusData);
      setActivityFeed(activities);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      flex: 1,
      minWidth: '160px',
      transition: 'transform 0.2s',
      cursor: 'pointer',
    }}
    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>{title}</p>
          <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '8px 0 0 0' }}>
            {loading ? '...' : value}
          </p>
          {subtitle && (
            <p style={{ fontSize: '11px', color: '#94a3b8', margin: '4px 0 0 0' }}>{subtitle}</p>
          )}
        </div>
        <div style={{
          backgroundColor: color,
          borderRadius: '50%',
          width: '44px',
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <span style={{ fontSize: '20px' }}>{icon}</span>
        </div>
      </div>
    </div>
  );

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const diff = Math.floor((new Date() - new Date(dateString)) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const COLORS = ['#f97316', '#10b981', '#3b82f6', '#ef4444', '#f59e0b', '#8b5cf6'];

  if (loading && recentRides.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Dashboard Overview</h1>
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '13px', color: '#64748b', flexWrap: 'wrap' }}>
            <span>🔄 Last updated: {lastUpdated || '—'}</span>
            <span style={{ color: '#10b981' }}>🟢 {stats.activeRides} active rides</span>
            {error && <span style={{ color: '#ef4444' }}>⚠️ {error}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#64748b' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <button
            onClick={fetchData}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '14px 18px',
          borderRadius: '10px',
          marginBottom: '16px',
          border: '1px solid #fecaca',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        <StatCard 
          title="Total Customers" 
          value={stats.totalCustomers} 
          icon="👥" 
          color="#dbeafe" 
        />
        <StatCard 
          title="Total Drivers" 
          value={stats.totalDrivers} 
          icon="🛵" 
          color="#dcfce7" 
        />
        <StatCard 
          title="Total Rides" 
          value={stats.totalRides} 
          icon="🚗" 
          color="#fef3c7" 
          subtitle={`${stats.completionRate.toFixed(0)}% completed`}
        />
        <StatCard 
          title="Today's Rides" 
          value={stats.todayRides} 
          icon="📅" 
          color="#f1f5f9" 
        />
        <StatCard 
          title="Total Revenue" 
          value={`UGX ${stats.totalRevenue?.toLocaleString() || 0}`} 
          icon="💰" 
          color="#fce7f3" 
        />
        <StatCard 
          title="Today's Revenue" 
          value={`UGX ${stats.todayRevenue?.toLocaleString() || 0}`} 
          icon="💵" 
          color="#e0e7ff" 
        />
        <StatCard 
          title="Pending Drivers" 
          value={stats.pendingDrivers} 
          icon="⏳" 
          color="#fef3c7" 
        />
        <StatCard 
          title="Active Rides" 
          value={stats.activeRides} 
          icon="🟢" 
          color="#dcfce7" 
        />
      </div>

      {/* Charts Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        marginBottom: '24px',
      }}>
        {/* Revenue Trend */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          height: '300px',
        }}>
          <h3 style={{ marginTop: 0, fontSize: '16px' }}>Revenue Trend (Last 7 Days)</h3>
          <ResponsiveContainer width="100%" height="85%">
            <AreaChart data={revenueTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Area 
                type="monotone" 
                dataKey="revenue" 
                stroke="#f97316" 
                fill="#f97316" 
                fillOpacity={0.2} 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Ride Status Distribution */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          height: '300px',
        }}>
          <h3 style={{ marginTop: 0, fontSize: '16px' }}>Ride Status Distribution</h3>
          {rideStatusData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
              No ride data available
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="85%">
              <PieChart>
                <Pie
                  data={rideStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={{ stroke: '#94a3b8' }}
                >
                  {rideStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Activity Feed & Recent Rides */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        gap: '24px',
        marginBottom: '24px',
      }}>
        {/* Activity Feed */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          maxHeight: '400px',
          overflow: 'auto',
        }}>
          <h3 style={{ marginTop: 0, fontSize: '16px' }}>📡 Live Activity</h3>
          {activityFeed.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
              No activity yet
            </div>
          ) : (
            activityFeed.map((activity, index) => (
              <div
                key={activity.id || index}
                style={{
                  padding: '10px 0',
                  borderBottom: index < activityFeed.length - 1 ? '1px solid #f1f5f9' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: activity.color + '20',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                }}>
                  {activity.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '14px' }}>{activity.title}</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>{activity.description}</div>
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                  {getTimeAgo(activity.time)}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Recent Rides */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          maxHeight: '400px',
          overflow: 'auto',
        }}>
          <h3 style={{ marginTop: 0, fontSize: '16px' }}>🚗 Recent Rides</h3>
          {recentRides.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
              No rides yet
            </div>
          ) : (
            recentRides.map((ride) => (
              <div
                key={ride.id}
                style={{
                  padding: '10px 0',
                  borderBottom: '1px solid #f1f5f9',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontWeight: '600', fontSize: '14px' }}>
                    #{ride.id} - {ride.customer_name || 'Customer'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    UGX {ride.total_fare?.toLocaleString() || 0}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    padding: '2px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    backgroundColor: ride.status === 'completed' ? '#dcfce7' :
                                  ride.status === 'cancelled' ? '#fee2e2' : '#fef3c7',
                    color: ride.status === 'completed' ? '#166534' :
                           ride.status === 'cancelled' ? '#991b1b' : '#92400e',
                  }}>
                    {ride.status || 'pending'}
                  </span>
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>
                    {getTimeAgo(ride.created_at)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default Overview;